import { computed, inject, Injectable, linkedSignal, Signal, signal, WritableSignal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { form } from "@angular/forms/signals";
import { GreenHomeBasisType, MarketFilterModel, StoredMarketFilterModel, TemporaryArea } from "@models/map/market-filter.model";
import { ILatLng } from "polydraw";
import {
    buildBuildYearModel,
    BuildYearFilterModel,
    createBuildYearModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-buildyear/search-filter-buildyear.model";
import {
    buildEnergyLabelsModel,
    createEnergyLabelsModel,
    EnergyLabelsFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-energy-labels/search-filter-energy-labels.model";
import {
    buildEstateTypesModel,
    createEstateTypesModel,
    EstateTypesFilterModel,
    EVEstateFilterType,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-estate-types/search-filter-estate-types.model";
import {
    buildFacilitiesTypesModel,
    createFacilitiesTypesModel,
    EVEstateFeatureType,
    FacilitiesTypesFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-facilities/search-filter-facilities.model";
import { buildFloorModel, createFloorModel, FloorFilterModel } from "src/app/ev-map/estate-map/estate-filter/search-filter-floor/search-filter-floor.model";
import {
    buildGreenHousingModel,
    createGreenHousingModel,
    GreenHousingFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-green-housing/search-filter-green-housing.model";
import {
    buildNumberOfBedroomModel,
    createNumberOfBedroomModel,
    NumberOfBedroomFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-numberofbedroom/search-filter-numberofbedroom.model";
import {
    buildOwnershipTypesModel,
    createOwnershipTypesModel,
    OwnershipTypesFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-ownership/search-filter-ownership.model";
import {
    buildPlotAreaModel,
    createPlotAreaModel,
    PlotAreaFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-plotarea/search-filter-plotarea.model";
import { buildPriceModel, createPriceModel, PriceFilterModel } from "src/app/ev-map/estate-map/estate-filter/search-filter-price/search-filter-price.model";
import {
    buildSiteTypesModel,
    createSiteTypesModel,
    SiteTypesFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-sitetype/search-filter-sitetype.model";
import {
    buildSqmPriceModel,
    createSqmPriceModel,
    SqmPriceFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-sqmprice/search-filter-sqmprice.model";
import {
    buildTimePeriodModel,
    createTimePeriodModel,
    TimePeriodFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-time-period/search-filter-time-period.model";
import {
    buildTimeOnMarketModel,
    createTimeOnMarketModel,
    TimeOnMarketFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-timeonmarket/search-filter-timeonmarket.model";
import {
    buildUnitAreaModel,
    createUnitAreaModel,
    UnitAreaFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-unitarea/search-filter-unitarea.model";
import {
    buildSearchViewOptionsModel,
    createSearchViewOptionsModel,
    SearchViewOptionsFilterModel,
} from "src/app/ev-map/estate-map/estate-filter/search-filter-view-options/search-filter-view-options.model";
import { EVEstateType } from "src/models/units/unit-attributes.model";
import { PolygonUtil } from "src/util/map/polygon.util";
import { CompareUtil } from "src/util/tools/compare.util";
import { FilterType } from "src/models/shared/filter.model";
import { AreaBuilderDataService } from "../area-builder-data.service";
import { MapStateService } from "../map/map-state.service";
import { StoredFiltersDataService } from "./stored-filters-data.service";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { filter as rxFilter, take } from "rxjs/operators";

// Opt in to "Mitt filter" over "Basis filter" on initial load. Unique profiles only.
const UserFilterQueryParam = "userfilter";

// The API requires a name on every stored filter, but the user never names their own filter — it is
// identified by being the default one, not by its name.
const UserFilterName = "UserFilter";

// The two sides of the filter toggle. A third, favorite-list mode exists but is not a FilterMode —
// it is owned by isFavoriteListFilterMode and takes the form over from whichever side is active.
export enum FilterMode {
    User = "user",
    Standard = "standard",
}

@Injectable({
    providedIn: "root",
})
export class MarketSalesStateService {
    private readonly mapStateService = inject(MapStateService);
    private readonly areaBuilderService = inject(AreaBuilderDataService);
    private readonly storedFiltersService = inject(StoredFiltersDataService);
    private readonly authorizationService = inject(EvAuthorizationService);
    private readonly router = inject(Router);

    private readonly uniqueUserConfirmed$ = toObservable(this.authorizationService.isUniqueUser).pipe(rxFilter(Boolean), take(1));

    sortOrderSignal: WritableSignal<string> = signal("-sortDate");

    private createDefaultFilterModel(): EstateFilterModel {
        return {
            price: createPriceModel()(),
            buildYear: createBuildYearModel()(),
            sqmPrice: createSqmPriceModel()(),
            unitArea: createUnitAreaModel()(),
            timeOnMarket: createTimeOnMarketModel()(),
            numberOfBedroom: createNumberOfBedroomModel()(),
            floor: createFloorModel()(),
            plotArea: createPlotAreaModel()(),
            timePeriod: createTimePeriodModel()(),
            estateTypes: createEstateTypesModel()(),
            ownershipTypes: createOwnershipTypesModel()(),
            siteTypes: createSiteTypesModel()(),
            facilitiesTypes: createFacilitiesTypesModel()(),
            energylabels: createEnergyLabelsModel()(),
            greenHousing: createGreenHousingModel()(),
            searchViewOptions: createSearchViewOptionsModel()(),
        };
    }

    filterModel: WritableSignal<EstateFilterModel> = signal(this.createDefaultFilterModel());

    defaultFilterModel: Signal<EstateFilterModel> = computed(() => this.createDefaultFilterModel());
    storedFilterModel: WritableSignal<EstateFilterModel> = signal(this.createDefaultFilterModel());

    // True while a favorite list owns the filter form.
    isFavoriteListFilterMode: WritableSignal<boolean> = signal(false);

    // Which side of the toggle the user is on. Only meaningful while isFavoriteListFilterMode is false.
    private filterModeSignal: WritableSignal<FilterMode> = signal(FilterMode.Standard);
    filterMode: Signal<FilterMode> = this.filterModeSignal.asReadonly();

    // The user's working filter, parked while the other side of the toggle — or a favorite list —
    // owns the form, so no edits are lost by switching.
    private userFilterSnapshot: WritableSignal<EstateFilterModel> = signal(this.createDefaultFilterModel());

    // Same idea for the standard side: edits made there survive a round trip to "Mitt filter".
    private standardFilterSnapshot: WritableSignal<EstateFilterModel> = signal(this.createDefaultFilterModel());

    // "Last saved" baseline for the user's filter — used for dirty-check and Undo.
    // Equal to default until a persisted user filter has been loaded.
    userSavedFilterModel: WritableSignal<EstateFilterModel> = signal(this.createDefaultFilterModel());

    // Id of the user's stored filter, once one is known. Null means nothing is stored yet, which is
    // what makes saveUserFilter choose between CreateUserFilter and the update endpoint.
    private userFilterId: WritableSignal<number | null> = signal(null);

    // Guards the once-per-session fetch: the filter panel is destroyed and recreated every time it
    // is closed and reopened, and re-applying the saved filter would discard unsaved edits.
    private userFilterRequested = false;

    filterForm = form(this.filterModel, (schemaPath) => {
        buildPriceModel(schemaPath.price);
        buildBuildYearModel(schemaPath.buildYear);
        buildSqmPriceModel(schemaPath.sqmPrice);
        buildUnitAreaModel(schemaPath.unitArea);
        buildTimeOnMarketModel(schemaPath.timeOnMarket);
        buildNumberOfBedroomModel(schemaPath.numberOfBedroom);
        buildFloorModel(schemaPath.floor);
        buildPlotAreaModel(schemaPath.plotArea);
        buildTimePeriodModel(schemaPath.timePeriod);
        buildEstateTypesModel(schemaPath.estateTypes);
        buildOwnershipTypesModel(schemaPath.ownershipTypes);
        buildSiteTypesModel(schemaPath.siteTypes);
        buildFacilitiesTypesModel(schemaPath.facilitiesTypes);
        buildEnergyLabelsModel(schemaPath.energylabels);
        buildGreenHousingModel(schemaPath.greenHousing);
        buildSearchViewOptionsModel(schemaPath.searchViewOptions);
    });

    isTimePeriodValid: Signal<boolean> = computed(() => !this.filterForm.timePeriod().invalid());

    /**
     * Validitet for hele filterskjemaet, ikke bare tidsperioden. Rot-feltets `invalid()` aggregerer
     * feil fra alle underfelt, slik at min/maks-validatorene på range-filtrene (pris, areal, etasje,
     * byggeår osv.) også holder igjen API-kall.
     */
    isFilterValid: Signal<boolean> = computed(() => !this.filterForm().invalid());

    private filterFormAPIRaw: Signal<MarketFilterModel> = computed((): MarketFilterModel => {
        const selectedEstateTypes = this.selectedEstateTypesSignal();
        const selectedOwnerShipTypes = this.selectedOwnerShipTypesSignal();
        const selectedPlotOwnerShipTypes = this.selectedPlotOwnerShipTypesSignal();
        const selectedAreas = this.selectedAreas();
        const selectedPolygon = this.selectedPolygon();
        const selectedGreenHomeBasis = this.selectedGreenHomeBasisSignal();

        return {
            priceFrom: this.filterForm.price.priceFrom().value() != "" ? +this.filterForm.price.priceFrom().value() * 1000000 : null,
            priceTo:
                this.filterForm.price.priceTo().value() != "∞" && this.filterForm.price.priceTo().value() != ""
                    ? +this.filterForm.price.priceTo().value() * 1000000
                    : null,
            buildYearFrom: this.filterForm.buildYear.buildYearFrom().value() != "" ? +this.filterForm.buildYear.buildYearFrom().value() : null,
            buildYearTo:
                this.filterForm.buildYear.buildYearTo().value() != "∞" && this.filterForm.buildYear.buildYearTo().value() != ""
                    ? +this.filterForm.buildYear.buildYearTo().value()
                    : null,
            sqmPriceFrom: this.filterForm.sqmPrice.sqmPriceFrom().value() != "" ? +this.filterForm.sqmPrice.sqmPriceFrom().value() : null,
            sqmPriceTo:
                this.filterForm.sqmPrice.sqmPriceTo().value() != "∞" && this.filterForm.sqmPrice.sqmPriceTo().value() != ""
                    ? +this.filterForm.sqmPrice.sqmPriceTo().value()
                    : null,
            unitAreaFrom: this.filterForm.unitArea.unitAreaFrom().value() != "" ? +this.filterForm.unitArea.unitAreaFrom().value() : null,
            unitAreaTo:
                this.filterForm.unitArea.unitAreaTo().value() != "∞" && this.filterForm.unitArea.unitAreaTo().value() != ""
                    ? +this.filterForm.unitArea.unitAreaTo().value()
                    : null,
            timeOnMarketFrom: this.filterForm.timeOnMarket.timeOnMarketFrom().value() != "" ? +this.filterForm.timeOnMarket.timeOnMarketFrom().value() : null,
            timeOnMarketTo:
                this.filterForm.timeOnMarket.timeOnMarketTo().value() != "∞" && this.filterForm.timeOnMarket.timeOnMarketTo().value() != ""
                    ? +this.filterForm.timeOnMarket.timeOnMarketTo().value()
                    : null,
            numberOfBedroomFrom:
                this.filterForm.numberOfBedroom.numberOfBedroomFrom().value() != "" ? +this.filterForm.numberOfBedroom.numberOfBedroomFrom().value() : null,
            numberOfBedroomTo:
                this.filterForm.numberOfBedroom.numberOfBedroomTo().value() != "∞" && this.filterForm.numberOfBedroom.numberOfBedroomTo().value() != ""
                    ? +this.filterForm.numberOfBedroom.numberOfBedroomTo().value()
                    : null,
            floorNumberFrom: this.filterForm.floor.floorNumberFrom().value() != "" ? +this.filterForm.floor.floorNumberFrom().value() : null,
            floorNumberTo:
                this.filterForm.floor.floorNumberTo().value() != "∞" && this.filterForm.floor.floorNumberTo().value() != ""
                    ? +this.filterForm.floor.floorNumberTo().value()
                    : null,
            plotAreaFrom: this.filterForm.plotArea.plotAreaFrom().value() != "" ? +this.filterForm.plotArea.plotAreaFrom().value() : null,
            plotAreaTo:
                this.filterForm.plotArea.plotAreaTo().value() != "∞" && this.filterForm.plotArea.plotAreaTo().value() != ""
                    ? +this.filterForm.plotArea.plotAreaTo().value()
                    : null,
            fromDate: this.filterForm.timePeriod.fromDate().value(),
            toDate: this.filterForm.timePeriod.toDate().value(),
            isWithDrawn: this.filterForm.searchViewOptions.includeWithDrawnPrices().value(),
            energyLabels: this.filterForm.energylabels
                .energyLabels()
                .value()
                .filter((label) => label.selected)
                .map((label) => label.energyLabel),
            isGreenHome: this.filterForm.greenHousing.isGreenHome().value(),
            greenHomeBasisTypes: this.filterForm.greenHousing.isGreenHome().value() === true ? selectedGreenHomeBasis : [],
            areas: selectedAreas,
            sortOrder: this.sortOrderSignal(), //TODO - Add sorting into the green belt
            includeAgentSales: this.filterForm.searchViewOptions.includeAgentSales().value(),
            isForSale: this.filterForm.searchViewOptions.isForSale().value(),
            includeUnCertainPrices: this.filterForm.searchViewOptions.includeUnCertainPrices().value(),
            isSitePrice: this.filterForm.searchViewOptions.isSitePrice().value(),
            hasElevator: this.filterForm.facilitiesTypes
                .facilitiesTypes()
                .value()
                .some((facility) => facility.facilitiesType === EVEstateFeatureType.Elevator && facility.selected),
            hasParking: this.filterForm.facilitiesTypes
                .facilitiesTypes()
                .value()
                .some((facility) => facility.facilitiesType === EVEstateFeatureType.Parking && facility.selected),
            hasBalcony: this.filterForm.facilitiesTypes
                .facilitiesTypes()
                .value()
                .some((facility) => facility.facilitiesType === EVEstateFeatureType.Balcony && facility.selected),
            includeWithDrawnPrices: true,
            includeJudicialTransfers: this.filterForm.searchViewOptions.includeJudicialTransfers().value(),
            estateTypes: this.filterForm.searchViewOptions.isSitePrice().value() == true ? [] : selectedEstateTypes,
            ownershipTypes: selectedOwnerShipTypes,
            plotOwnershipTypes: selectedPlotOwnerShipTypes,
            monthsBackInTime: this.filterForm.timePeriod.useFromToDate().value() ? null : this.filterForm.timePeriod.numberOfMonthsToShow().value(),
            geoBorder: selectedPolygon,
            periodDefinitionType: this.filterForm.searchViewOptions.periodDefinitionType().value(),
            estateAgentHeadOfficeId:
                this.filterForm.searchViewOptions.includeOnlyEstateAgencySales().value() === true
                    ? this.filterForm.searchViewOptions.estateAgentHeadOfficeId().value()
                    : null,
            isOwnedByCompany: this.filterForm.searchViewOptions.isOwnedByCompany().value(),
        };
    });

    /**
     * Holder igjen forrige gyldige filter så lenge filterskjemaet er ugyldig. Konsumentene ser da
     * uendrede parametre, og verken rxResource-ene eller distinctUntilChanged trigger nye kall
     * før brukeren har rettet feilen.
     */
    filterFormAPI: Signal<MarketFilterModel> = linkedSignal<{ filter: MarketFilterModel; isValid: boolean }, MarketFilterModel>({
        source: () => ({ filter: this.filterFormAPIRaw(), isValid: this.isFilterValid() }),
        computation: ({ filter, isValid }, previous) => (isValid || previous === undefined ? filter : previous.value),
    });

    selectedEstateTypesSignal: Signal<number[]> = computed(() => {
        const selectedTypes: number[] = [];
        this.filterForm.estateTypes
            .estateTypes()
            .value()
            .forEach((estateType) => {
                if (estateType.selected) {
                    selectedTypes.push(estateType.selected ? estateType.index : null);

                    if (estateType.estateType === EVEstateFilterType.Annet) {
                        selectedTypes.push(EVEstateType.Garasje);
                    }
                }
            });
        return selectedTypes;
    });

    selectedOwnerShipTypesSignal: Signal<number[]> = computed(() => {
        const selectedTypes: number[] = [1];
        this.filterForm.ownershipTypes
            .ownershipTypes()
            .value()
            .forEach((ownershipType) => {
                if (ownershipType.selected) {
                    selectedTypes.push(ownershipType.selected ? ownershipType.index : null);
                }
            });
        return selectedTypes;
    });

    selectedPlotOwnerShipTypesSignal: Signal<number[]> = computed(() => {
        const selectedTypes: number[] = [];
        this.filterForm.siteTypes
            .siteTypes()
            .value()
            .forEach((siteType) => {
                if (siteType.selected) {
                    selectedTypes.push(siteType.selected ? siteType.index : null);
                }
            });
        return selectedTypes;
    });

    selectedPolygon: Signal<ILatLng[][][]> = computed(() => {
        let polygon = null;
        const mapData = this.areaBuilderService.getAreasAndPolygons();
        if (
            mapData.areas?.length === 0 &&
            mapData.selfDrawnPolygons?.length === 0 &&
            this.mapStateService.mapUpdateBoundsOrPolygonSignal().state.getMapBoundParams()
        ) {
            polygon = PolygonUtil.createPolygonFromBounds(this.mapStateService.mapUpdateBoundsOrPolygonSignal().state.getMapBoundParams());
        } else {
            polygon = mapData.selfDrawnPolygons;
        }
        return polygon;
    });

    selectedAreas: Signal<TemporaryArea[]> = computed(() => {
        let areas: TemporaryArea[] = [];
        const mapData = this.areaBuilderService.getAreasAndPolygons();
        if (mapData.areas?.length != 0) {
            areas = mapData.areas;
        }

        return areas;
    });

    // opening or closing a section must not make the filter look edited.
    private withoutSectionExpandedState(model: EstateFilterModel): Record<string, unknown> {
        const stripped: Record<string, unknown> = {};
        for (const [key, section] of Object.entries(model)) {
            const { expanded, ...criteria } = section as Record<string, unknown>;
            stripped[key] = criteria;
        }
        return stripped;
    }

    private filterCriteriaEquals(a: EstateFilterModel, b: EstateFilterModel): boolean {
        return CompareUtil.deepEquals(this.withoutSectionExpandedState(a), this.withoutSectionExpandedState(b));
    }

    isDirty = computed(() => {
        return !this.filterCriteriaEquals(this.filterForm().value(), this.defaultFilterModel());
    });

    isFavoriteListFilterDirty = computed(() => {
        return !this.filterCriteriaEquals(this.filterForm().value(), this.storedFilterModel());
    });

    isUserFilterDirty = computed(() => {
        if (this.isFavoriteListFilterMode() || this.filterMode() !== FilterMode.User) return false;
        return !this.filterCriteriaEquals(this.filterForm().value(), this.userSavedFilterModel());
    });

    isStandardFilterDirty = computed(() => {
        if (this.isFavoriteListFilterMode() || this.filterMode() !== FilterMode.Standard) return false;
        return !this.filterCriteriaEquals(this.filterForm().value(), this.defaultFilterModel());
    });

    selectedGreenHomeBasisSignal: Signal<GreenHomeBasisType[]> = computed(() => {
        return this.filterForm.greenHousing
            .greenHomeBasisTypes()
            .value()
            .filter((x) => x.selected)
            .map((x) => x.greenHomeBasisType);
    });

    setFilterFromStoredModel(storedFilter: MarketFilterModel): void {
        this.storedFilterModel.update((current) => this.applyStoredFilter(current, storedFilter));
        this.filterModel.update((current) => this.applyStoredFilter(current, storedFilter));

        if (storedFilter.sortOrder) {
            this.sortOrderSignal.set(storedFilter.sortOrder);
        }
    }

    setFilterFromUserFilter(userFilter: MarketFilterModel): void {
        this.filterForm().reset();
        this.filterModel.update((current) => this.applyStoredFilter(current, userFilter));
        this.userSavedFilterModel.set(this.filterModel());

        if (userFilter.sortOrder) {
            this.sortOrderSignal.set(userFilter.sortOrder);
        }
    }

    private applyStoredFilter(current: EstateFilterModel, storedFilter: MarketFilterModel): EstateFilterModel {
        return {
            ...current,
            price: {
                ...current.price,
                priceFrom: storedFilter.priceFrom != null ? String(storedFilter.priceFrom / 1000000) : "",
                priceTo: storedFilter.priceTo != null ? String(storedFilter.priceTo / 1000000) : "∞",
            },
            sqmPrice: {
                ...current.sqmPrice,
                sqmPriceFrom: storedFilter.sqmPriceFrom != null ? String(storedFilter.sqmPriceFrom) : "",
                sqmPriceTo: storedFilter.sqmPriceTo != null ? String(storedFilter.sqmPriceTo) : "∞",
            },
            buildYear: {
                ...current.buildYear,
                buildYearFrom: storedFilter.buildYearFrom != null ? String(storedFilter.buildYearFrom) : "",
                buildYearTo: storedFilter.buildYearTo != null ? String(storedFilter.buildYearTo) : "∞",
            },
            unitArea: {
                ...current.unitArea,
                unitAreaFrom: storedFilter.unitAreaFrom != null ? String(storedFilter.unitAreaFrom) : "",
                unitAreaTo: storedFilter.unitAreaTo != null ? String(storedFilter.unitAreaTo) : "∞",
            },
            timeOnMarket: {
                ...current.timeOnMarket,
                timeOnMarketFrom: storedFilter.timeOnMarketFrom != null ? String(storedFilter.timeOnMarketFrom) : "",
                timeOnMarketTo: storedFilter.timeOnMarketTo != null ? String(storedFilter.timeOnMarketTo) : "∞",
            },
            numberOfBedroom: {
                ...current.numberOfBedroom,
                numberOfBedroomFrom: storedFilter.numberOfBedroomFrom != null ? String(storedFilter.numberOfBedroomFrom) : "",
                numberOfBedroomTo: storedFilter.numberOfBedroomTo != null ? String(storedFilter.numberOfBedroomTo) : "∞",
            },
            floor: {
                ...current.floor,
                floorNumberFrom: storedFilter.floorNumberFrom != null ? String(storedFilter.floorNumberFrom) : "",
                floorNumberTo: storedFilter.floorNumberTo != null ? String(storedFilter.floorNumberTo) : "∞",
            },
            plotArea: {
                ...current.plotArea,
                plotAreaFrom: storedFilter.plotAreaFrom != null ? String(storedFilter.plotAreaFrom) : "",
                plotAreaTo: storedFilter.plotAreaTo != null ? String(storedFilter.plotAreaTo) : "∞",
            },
            timePeriod: {
                ...current.timePeriod,
                fromDate: storedFilter.fromDate,
                toDate: storedFilter.toDate,
                useFromToDate: storedFilter.fromDate != null || storedFilter.toDate != null,
                numberOfMonthsToShow: storedFilter.monthsBackInTime ?? 12,
            },
            estateTypes: {
                ...current.estateTypes,
                estateTypes: current.estateTypes.estateTypes.map((et) => ({
                    ...et,
                    selected: storedFilter.estateTypes.includes(et.index),
                })),
            },
            ownershipTypes: {
                ...current.ownershipTypes,
                ownershipTypes: current.ownershipTypes.ownershipTypes.map((ot) => ({
                    ...ot,
                    selected: storedFilter.ownershipTypes.includes(ot.index),
                })),
            },
            siteTypes: {
                ...current.siteTypes,
                siteTypes: current.siteTypes.siteTypes.map((st) => ({
                    ...st,
                    selected: storedFilter.plotOwnershipTypes.includes(st.index),
                })),
            },
            facilitiesTypes: {
                ...current.facilitiesTypes,
                facilitiesTypes: current.facilitiesTypes.facilitiesTypes.map((ft) => ({
                    ...ft,
                    selected:
                        (ft.facilitiesType === EVEstateFeatureType.Elevator && storedFilter.hasElevator === true) ||
                        (ft.facilitiesType === EVEstateFeatureType.Parking && storedFilter.hasParking === true) ||
                        (ft.facilitiesType === EVEstateFeatureType.Balcony && storedFilter.hasBalcony === true),
                })),
            },
            energylabels: {
                ...current.energylabels,
                energyLabels: current.energylabels.energyLabels.map((el) => ({
                    ...el,
                    selected: storedFilter.energyLabels.includes(el.energyLabel),
                })),
            },
            greenHousing: {
                ...current.greenHousing,
                isGreenHome: storedFilter.isGreenHome ?? false,
                greenHomeBasisTypes: current.greenHousing.greenHomeBasisTypes.map((gb) => ({
                    ...gb,
                    selected: storedFilter.greenHomeBasisTypes.includes(gb.greenHomeBasisType),
                })),
            },
            searchViewOptions: {
                ...current.searchViewOptions,
                isSitePrice: storedFilter.isSitePrice ?? false,
                periodDefinitionType: storedFilter.periodDefinitionType ?? 3,
                includeJudicialTransfers: storedFilter.includeJudicialTransfers ?? true,
                includeAgentSales: storedFilter.includeAgentSales ?? true,
                includeWithDrawnPrices: storedFilter.isWithDrawn ?? false,
                isForSale: storedFilter.isForSale ?? false,
                isOwnedByCompany: storedFilter.isOwnedByCompany ?? false,
                includeUnCertainPrices: storedFilter.includeUnCertainPrices ?? true,
                // The API model has no flag of its own: an id is only ever stored when the checkbox was
                // on. Restoring the checkbox is what actually matters — search-filter-view-options
                // derives estateAgentHeadOfficeId from it, and would otherwise overwrite the id below
                // with whatever the checkbox happened to be left at.
                includeOnlyEstateAgencySales: storedFilter.estateAgentHeadOfficeId != null,
                estateAgentHeadOfficeId: storedFilter.estateAgentHeadOfficeId,
            },
        };
    }

    private snapshotActiveMode(): void {
        const current = this.filterModel();
        if (this.filterMode() === FilterMode.User) {
            this.userFilterSnapshot.set(current);
        } else {
            this.standardFilterSnapshot.set(current);
        }
    }

    private snapshotForMode(mode: FilterMode): EstateFilterModel {
        return mode === FilterMode.User ? this.userFilterSnapshot() : this.standardFilterSnapshot();
    }

    setFilterMode(mode: FilterMode): void {
        if (this.isFavoriteListFilterMode()) return;
        if (mode === FilterMode.User && !this.authorizationService.isUniqueUser()) return;
        if (mode === this.filterMode()) return;

        this.snapshotActiveMode();
        this.filterForm().reset();
        this.filterModel.set(this.snapshotForMode(mode));
        this.filterModeSignal.set(mode);
    }

    enterFavoriteListFilterMode(): void {
        if (this.isFavoriteListFilterMode()) return;

        this.snapshotActiveMode();
        this.isFavoriteListFilterMode.set(true);
    }

    exitFavoriteListFilterMode(): void {
        if (!this.isFavoriteListFilterMode()) return;

        this.isFavoriteListFilterMode.set(false);
        this.storedFilterModel.set(this.createDefaultFilterModel());
        this.filterForm().reset();
        this.filterModel.set(this.snapshotForMode(this.filterMode()));
    }

    // A new, unsaved list starts from whatever filter the user currently sees — that becomes its baseline.
    setFavoriteListBaselineFromCurrentFilter(): void {
        this.storedFilterModel.set(this.filterModel());
    }

    loadUserFilter(): void {
        if (this.userFilterRequested) return;
        this.userFilterRequested = true;

        if (this.isUserFilterRequestedByUrl()) {
            this.uniqueUserConfirmed$.subscribe(() => this.setFilterMode(FilterMode.User));
        }

        this.storedFiltersService.getUserFilter(FilterType.Market).subscribe((userFilter) => {
            if (userFilter == null) return;

            this.userFilterId.set(userFilter.id ?? null);

            if (this.isFavoriteListFilterMode() || this.filterMode() === FilterMode.Standard) {
                const applied = this.applyStoredFilter(this.userFilterSnapshot(), userFilter);
                this.userFilterSnapshot.set(applied);
                this.userSavedFilterModel.set(applied);
                return;
            }

            this.setFilterFromUserFilter(userFilter);

            if (userFilter.geoBorder?.length > 0 || userFilter.areas?.length > 0) {
                this.areaBuilderService.positionMapToAreasAndPolygons(userFilter.areas ?? [], userFilter.geoBorder ?? []);
            }
        });
    }

    private isUserFilterRequestedByUrl(): boolean {
        const paramMap = this.router.parseUrl(this.router.url).queryParamMap;
        if (!paramMap.has(UserFilterQueryParam)) return false;

        const value = paramMap.get(UserFilterQueryParam);
        return value == null || value === "" || value.toLowerCase() === "true" || value === "1";
    }

    saveUserFilter(): void {
        if (!this.authorizationService.isUniqueUser()) return;

        const filter: StoredMarketFilterModel = {
            name: UserFilterName,
            filterType: FilterType.Market,
            unitIds: [],
            ...this.filterFormAPI(),
            // The user filter stores criteria only, never a geographic selection.
            geoBorder: [],
            areas: [],
        };
        const savedModel = this.filterModel();
        const filterId = this.userFilterId();

        const request =
            filterId == null ? this.storedFiltersService.createUserFilter(filter) : this.storedFiltersService.updateFilter(filterId, filter);

        request.subscribe((saved) => {
            this.userFilterId.set(saved.id);
            this.userSavedFilterModel.set(savedModel);
        });
    }

    undoUserFilter(): void {
        this.filterForm().reset();
        this.filterModel.set(this.userSavedFilterModel());
    }

    undoStandardFilter(): void {
        this.filterForm().reset();
        this.filterModel.set(this.defaultFilterModel());
        this.standardFilterSnapshot.set(this.filterModel());
    }

    // Makes sure the estate type the user navigated to is not hidden behind the filter
    ensureEstateTypeSelected(estateType: EVEstateType | null | undefined): void {
        if (estateType == null) return;
        if (this.isFavoriteListFilterMode() || this.filterMode() !== FilterMode.Standard) return;

        const estateTypes = this.filterForm.estateTypes.estateTypes().value();
        const match = estateTypes.find((type) => !type.other && type.index === estateType);
        if (match == null || match.selected) return;

        this.filterForm.estateTypes
            .estateTypes()
            .value.set(estateTypes.map((type) => (type.index === estateType ? { ...type, selected: true } : type)));
    }

    // Hard reset: back to the hard-coded standard values, regardless of what the user has saved.
    resetUserFilterToStandard(): void {
        this.filterForm().reset();
        this.filterModel.set(this.defaultFilterModel());
    }

    isAllowedEstateTypeMix = computed(() => {
        const estateTypes = this.filterForm().value().estateTypes;
        const estateTypesKeys = Object.keys(estateTypes.estateTypes).filter((key: string) =>
            estateTypes.estateTypes.find((x) => x.estateType == (key as EVEstateFilterType)),
        );
        return !(estateTypesKeys.length > 1 && estateTypesKeys.includes(EVEstateType.Fritidsbolig.toString()));
    });
}

export interface EstateFilterModel {
    price: PriceFilterModel;
    buildYear: BuildYearFilterModel;
    sqmPrice: SqmPriceFilterModel;
    unitArea: UnitAreaFilterModel;
    timeOnMarket: TimeOnMarketFilterModel;
    numberOfBedroom: NumberOfBedroomFilterModel;
    floor: FloorFilterModel;
    plotArea: PlotAreaFilterModel;
    timePeriod: TimePeriodFilterModel;
    estateTypes: EstateTypesFilterModel;
    ownershipTypes: OwnershipTypesFilterModel;
    siteTypes: SiteTypesFilterModel;
    facilitiesTypes: FacilitiesTypesFilterModel;
    energylabels: EnergyLabelsFilterModel;
    greenHousing: GreenHousingFilterModel;
    searchViewOptions: SearchViewOptionsFilterModel;
}
